import {
  VaccinationActApiInterface as ProVaccinationActApiInterface,
  VaccinationAct as ProVaccinationAct,
  UpdateVaccinationActBodyVaccinationAct as ProUpdateVaccinationAct,
} from "@syadem/kairos-pro-js";
import {
  VaccinationActApiInterface as TeamVaccinationActApiInterface,
  VaccinationAct as TeamVaccinationAct,
  UpdateVaccinationActBodyVaccinationAct as TeamUpdateVaccinationAct,
} from "@syadem/kairos-team-js";
import { AppStore } from "../store";
import { Service } from "./service";
import { ActionResult } from "../actionResult";

interface Payload {
  vaccinationAct: ProVaccinationAct | TeamVaccinationAct;
  updatedVaccinationAct: ProUpdateVaccinationAct | TeamUpdateVaccinationAct;
  organizationId: string | null;
  teamId: string | null;
  healthRecordId: string;
}

function normalizeVaccinationAct<T extends ProUpdateVaccinationAct | TeamUpdateVaccinationAct>(vaccinationAct: T): T {
  const normalizeValue = (value: unknown): unknown => {
    if (value === "" || value === undefined) return null;
    if (value instanceof Date) return value;
    if (Array.isArray(value)) return value.map(normalizeValue);

    if (value !== null && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value).map(([field, fieldValue]) => [field, normalizeValue(fieldValue)]),
      );
    }

    return value;
  };

  return normalizeValue(vaccinationAct) as T;
}

function valuesAreEqual(firstValue: unknown, secondValue: unknown): boolean {
  if (firstValue instanceof Date && secondValue instanceof Date) {
    return firstValue.getTime() === secondValue.getTime();
  }

  if (Array.isArray(firstValue) && Array.isArray(secondValue)) {
    return (
      firstValue.length === secondValue.length &&
      firstValue.every((value, index) => valuesAreEqual(value, secondValue[index]))
    );
  }

  if (
    firstValue !== null &&
    secondValue !== null &&
    typeof firstValue === "object" &&
    typeof secondValue === "object"
  ) {
    const firstEntries = Object.entries(firstValue);
    const secondEntries = Object.entries(secondValue);
    return (
      firstEntries.length === secondEntries.length &&
      firstEntries.every(([field, value]) => valuesAreEqual(value, (secondValue as Record<string, unknown>)[field]))
    );
  }

  return firstValue === secondValue;
}

function updatedFields(
  vaccinationAct: ProVaccinationAct | TeamVaccinationAct,
  updatedVaccinationAct: ProUpdateVaccinationAct | TeamUpdateVaccinationAct,
): ProUpdateVaccinationAct | TeamUpdateVaccinationAct {
  const normalizedVaccinationAct = normalizeVaccinationAct(updatedVaccinationAct);

  return Object.fromEntries(
    Object.entries(normalizedVaccinationAct).filter(
      ([field, value]) =>
        !valuesAreEqual(value, vaccinationAct[field as keyof (ProVaccinationAct | TeamVaccinationAct)]),
    ),
  ) as ProUpdateVaccinationAct | TeamUpdateVaccinationAct;
}

export class UpdateVaccinationActService implements Service<Payload, ProVaccinationAct | TeamVaccinationAct> {
  constructor(
    private readonly proVaccinationactapi: ProVaccinationActApiInterface,
    private readonly teamVaccinationactapi: TeamVaccinationActApiInterface,
    private readonly store: AppStore,
  ) {}

  async call({
    vaccinationAct,
    updatedVaccinationAct,
    healthRecordId,
    organizationId,
    teamId,
  }: Payload): Promise<ActionResult<ProVaccinationAct | TeamVaccinationAct>> {
    let apiVaccinationAct: ProVaccinationAct | TeamVaccinationAct;
    const vaccinationActUpdates = updatedFields(vaccinationAct, updatedVaccinationAct);

    if (Object.keys(vaccinationActUpdates).length === 0) {
      return { type: "success", value: vaccinationAct };
    }

    if (organizationId && teamId) {
      apiVaccinationAct = (
        await this.teamVaccinationactapi.updateVaccinationAct(
          organizationId,
          teamId,
          healthRecordId,
          vaccinationAct.id,
          { vaccinationAct: vaccinationActUpdates as TeamUpdateVaccinationAct },
        )
      ).vaccinationAct;
    } else {
      apiVaccinationAct = (
        await this.proVaccinationactapi.updateVaccinationAct(healthRecordId, vaccinationAct.id, {
          vaccinationAct: vaccinationActUpdates as ProUpdateVaccinationAct,
        })
      ).vaccinationAct;
    }

    this.updateVaccinationActInStore(healthRecordId, apiVaccinationAct);

    return {
      type: "success",
      value: apiVaccinationAct,
    };
  }

  private updateVaccinationActInStore(healthRecordId: string, vaccinationAct: ProVaccinationAct | TeamVaccinationAct) {
    this.store.setState((state) => {
      if (state.healthRecords && state.healthRecords[healthRecordId]) {
        const vaccinationActToUpdateIndex = state.healthRecords[healthRecordId].vaccinationActs.findIndex(
          (va) => va.id === vaccinationAct.id,
        );
        state.healthRecords[healthRecordId].vaccinationActs[vaccinationActToUpdateIndex] = vaccinationAct;
      }
      return state;
    });
  }
}
